// PRC-027: «Volver» al origen real en Pricing, con el mismo mecanismo que
// Control comercial: el origen viaja en el parámetro `returnTo` de la dirección
// (y, por compatibilidad, en `state.returnTo`). Nunca navigate(-1).
import { useLocation, useNavigate } from 'react-router-dom';

export const RETURN_PARAM = 'returnTo';

// Pantallas de Pricing que pueden ser origen y cómo se nombran en «Volver …».
const ORIGINS: Array<{ prefix: string; label: string }> = [
  { prefix: '/primeros-pasos', label: 'Volver a Primeros pasos' },
  { prefix: '/bienvenida', label: 'Volver a la bienvenida' },
  { prefix: '/margin-center/overview', label: 'Volver a Overview' },
  { prefix: '/margin-center/explorer', label: 'Volver al Explorer' },
  { prefix: '/margin-center/customers/', label: 'Volver a la ficha del cliente' },
  { prefix: '/margin-center/products/', label: 'Volver a la ficha del producto' },
  { prefix: '/margin-center/control-comercial/planificacion', label: 'Volver a Revisiones programadas' },
  { prefix: '/margin-center/control-comercial/avisos/cadencia', label: 'Volver a Cadencia' },
  { prefix: '/margin-center/control-comercial/avisos/consumo', label: 'Volver a Consumo' },
  { prefix: '/margin-center/control-comercial/avisos/condiciones-economicas', label: 'Volver a Condiciones económicas' },
  { prefix: '/margin-center/control-comercial', label: 'Volver a Control comercial' },
  { prefix: '/optimization-center', label: 'Volver a Optimización' },
  { prefix: '/optimization/history', label: 'Volver al historial' },
  { prefix: '/optimization/evaluations/', label: 'Volver a la evaluación' },
  { prefix: '/optimization', label: 'Volver a Evaluación rápida' },
  { prefix: '/batch-optimization', label: 'Volver a Optimización por lotes' },
  { prefix: '/predictions', label: 'Volver a Planificación' },
  { prefix: '/operations', label: 'Volver a Operaciones' },
  { prefix: '/data-upload', label: 'Volver a Carga de datos' },
  { prefix: '/data-quality', label: 'Volver a Calidad de datos' },
  { prefix: '/connections', label: 'Volver a Conexiones' },
  { prefix: '/retraining', label: 'Volver a Modelos y actualizaciones' },
  { prefix: '/historical-costs', label: 'Volver a Costes' },
  { prefix: '/subscription', label: 'Volver a Mi suscripción' },
  { prefix: '/team', label: 'Volver a Mi equipo' },
  { prefix: '/profile', label: 'Volver a Mi perfil' },
];

const originOf = (path: string) => ORIGINS.find((o) => path === o.prefix || path.startsWith(`${o.prefix}?`) || path.startsWith(o.prefix.endsWith('/') ? o.prefix : `${o.prefix}/`) || path.startsWith(`${o.prefix}#`));

// Solo rutas internas de Pricing: nada externo ni «//dominio».
export function safeOrigin(path: string | null | undefined): string | null {
  if (!path || !path.startsWith('/') || path.startsWith('//')) return null;
  return originOf(path) ? path : null;
}

export function backLabel(path: string): string {
  return originOf(path)?.label ?? 'Volver';
}

// Ruta actual (con su búsqueda) para pasarla como origen.
export const currentOrigin = (location: { pathname: string; search: string }) => `${location.pathname}${location.search}`;

// Añade el origen a un destino, conservando su búsqueda.
export function withReturnTo(target: string, origin: string): string {
  const [path, query = ''] = target.split('?');
  const params = new URLSearchParams(query);
  params.set(RETURN_PARAM, origin);
  return `${path}?${params.toString()}`;
}

export interface BackTarget { to: string; label: string; fromOrigin: boolean }

// «Volver» de la pantalla actual: el origen real si llegó, si no la pantalla madre.
export function useBackTarget(fallback: string, fallbackLabel?: string): BackTarget & { go: () => void } {
  const location = useLocation();
  const navigate = useNavigate();
  const fromQuery = new URLSearchParams(location.search).get(RETURN_PARAM);
  const fromState = (location.state as { returnTo?: string } | null)?.returnTo;
  const origin = safeOrigin(fromQuery) ?? safeOrigin(fromState);
  const to = origin ?? fallback;
  const label = origin ? backLabel(origin) : (fallbackLabel ?? backLabel(fallback));
  return { to, label, fromOrigin: !!origin, go: () => navigate(to) };
}

